import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import RadioGroup from '@/components/forms/RadioGroup';
import Select from '@/components/forms/Select';
import Textarea from '@/components/forms/Textarea';
import { Upload } from '@/components/upload';
import { Separator } from '@/design-system/separator';
import { FileWithPreview } from '@/hooks/use-file-upload';
import { imageFileSchema } from '@/lib/uploadSchema';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { z } from 'zod';

const managePointsSchema = z.object({
  points: z.string().min(1, { message: 'Points are required' }),
  remarks: z.string().min(1, { message: 'Description is required' }),
  userType: z.union([z.literal('apple'), z.literal('orange')]),
  type: z.enum(['transfer', 'retrieve'], {
    required_error: 'You need to select a notification type.',
  }),
  user: z.union([z.literal('ball'), z.literal('cat')]),
  image: imageFileSchema.optional(),
});

export const ManagePoints = () => {
  const form = useForm({ schema: managePointsSchema, mode: 'onBlur', defaultValues: { type: 'transfer' } });
  const { t } = useDynamicLocale();

  const UserType: Record<z.infer<typeof managePointsSchema>['userType'], string> = {
    apple: 'Apple',
    orange: 'Orange',
  };

  const User: Record<z.infer<typeof managePointsSchema>['user'], string> = {
    ball: 'Ball',
    cat: 'Cat',
  };
  const TypeMap: Record<z.infer<typeof managePointsSchema>['type'], string> = {
    transfer: 'Transfer',
    retrieve: 'Retrieve',
  };

  const createOptions = <T extends Record<string, string>>(obj: T) =>
    Object.entries(obj).map(([value, label]) => ({ value, label }));

  const typeOptions = Object.entries(TypeMap).map(([value, label]) => ({
    value,
    label,
  }));

  const handleFilesChange = (files: FileWithPreview[]) => {
    if (files[0]?.file instanceof File) {
      form.setValue('image', files[0].file);
    } else {
      form.setValue('image', new File([], ''));
    }
  };

  const isTransfer = form.watch('type') === 'transfer';
  return (
    <Form
      form={form}
      onSubmit={() => {
        return new Promise((res) => {
          setTimeout(res, 5000);
        });
      }}
    >
      <RadioGroup groupLabel="Type" name="type" label="Type" options={typeOptions} />

      <div className="flex items-center gap-3 my-4">
        <div className="text-control font-bold">
          <Translation
            id={
              isTransfer
                ? 'pointManagement.page.form.managePoints.transfer'
                : 'pointManagement.page.form.managePoints.retrieve'
            }
          />
        </div>
        <Separator variant="muted" thickness="lg" />
      </div>

      <div className="w-full">
        <Select
          label="pointManagement.page.form.managePoints.userType"
          {...form.register('user')}
          options={createOptions(User)}
          placeholder={t('common.form.placeholder.select')}
        />

        <Select
          label="pointManagement.page.form.managePoints.user"
          {...form.register('userType')}
          options={createOptions(UserType)}
          placeholder={t('common.form.placeholder.select')}
        />
        <Input label="pointManagement.page.form.managePoints.points" {...form.register('points')} />
        <Textarea label="pointManagement.page.form.managePoints.remarks" {...form.register('remarks')} />
        <Upload
          onFilesChange={handleFilesChange}
          label="pointManagement.page.form.managePoints.image"
          {...form.register('image')}
          variant="rect"
        />
      </div>
    </Form>
  );
};
